import { EventEmitter } from './EventEmitter';
import { MAX_RECONNECT_ATTEMPTS } from '../config/env';

interface Vector3 {
    x: number;
    y: number;
    z: number;
}

interface Rotation {
    y: number;
}

interface GameData {
    name: string;
    isWalking: boolean;
    isDancing?: boolean;
    [key: string]: any;
}

interface Player {
    player_id: string;
    position: Vector3;
    rotation: Rotation;
    game_data: GameData;
}

interface ChatMessage {
    sender_id: string;
    sender_name: string;
    content: string;
    timestamp: number;
}

interface GameState {
    players: { [key: string]: Player };
    server_time: number;
    chat_history?: ChatMessage[];
}

interface GameEvents {
    connected: (data: any) => void;
    disconnected: () => void;
    gameState: (state: GameState) => void;
    latency: (latency: number) => void;
    message: (message: any) => void;
    player_joined: (data: any) => void;
    player_left: (data: any) => void;
    chat_message: (message: ChatMessage) => void;
}

export declare interface UDPGameClient {
    on<K extends keyof GameEvents>(event: K, listener: GameEvents[K]): this;
    emit<K extends keyof GameEvents>(event: K, ...args: Parameters<GameEvents[K]>): boolean;
}

export class UDPGameClient extends EventEmitter {
    private socket: WebSocket | null = null;
    private playerId: string;
    private serverTimeOffset: number = 0;
    private gameState: GameState = { players: {}, server_time: 0, chat_history: [] };
    private pingInterval: ReturnType<typeof setInterval> | null = null;
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = MAX_RECONNECT_ATTEMPTS;

    constructor(private serverUrl: string, private playerName: string = "Player") {
        super();
        this.playerId = `player-${Math.random().toString(36).substr(2, 9)}`;
        console.log('UDPGameClient initialized with ID:', this.playerId);
    }

    public async connect(): Promise<void> {
        try {
            console.log('Connecting to UDP server via WebSocket proxy...');
            // Add initial delay to allow server startup
            await new Promise(resolve => setTimeout(resolve, 1000));
            await this.setupWebSocket();
        } catch (error) {
            console.error('Failed to connect:', error);
            this.handleConnectionError();
        }
    }

    private async setupWebSocket(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log('Creating WebSocket connection');
            
            if (this.socket?.readyState === WebSocket.OPEN) {
                console.log('WebSocket already connected');
                resolve();
                return;
            }
            
            this.socket = new WebSocket(this.serverUrl);

            this.socket.onopen = () => {
                console.log('WebSocket connection established');
                console.log('Sending connect message...');
                this.sendConnectMessage();
                resolve();
            };

            this.socket.onclose = (event) => {
                console.log('WebSocket connection closed:', {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean
                });
                this.handleConnectionError();
            };

            this.socket.onerror = (error) => {
                console.error('WebSocket connection error:', {
                    error,
                    readyState: this.socket?.readyState
                });
                reject(error);
            };

            this.socket.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
 
                    this.handleMessage(message);
                } catch (error) {
                    console.error('Error processing message:', {
                        error,
                        data: event.data
                    });
                }
            };
        });
    }

    private handleMessage(message: any): void {
        switch (message.type) {
            case 'connect_ack':
                this.serverTimeOffset = message.server_time - Date.now();
                this.startPingInterval();
                console.log('Connected to server with ID:', message.player_id);
                this.emit('connected', message);
                break;

            case 'game_state':
                this.handleGameState(message);
                break;

            case 'player_joined':
                console.log('Player joined:', {
                    id: message.player_id,
                    position: message.position,
                    name: message.game_data?.name
                });
                this.emit('player_joined', message);
                break;

            case 'player_left':
                console.log('Player left:', {
                    id: message.player_id
                });
                this.emit('player_left', message);
                break;

            case 'pong':
                const latency = Date.now() - message.client_time;
                this.emit('latency', latency);
                break;

            case 'chat_message':
                console.log('Chat message received from server:', message);
                this.handleChatMessage(message);
                break;

            default:
                console.log('Received unknown message type:', message.type);
                this.emit('message', message);
                break;
        }
    }

    private handleGameState(message: any) {
        // Preserve the original message structure but ensure chat_history is included
        const gameState = {
            ...message,
            chat_history: message.chat_history || this.gameState.chat_history
        };
        
        // Update local game state
        this.gameState = gameState;
        
        // Emit the properly structured game state
        this.emit('gameState', gameState);
    }

    private handleChatMessage(message: any): void {
        const chatMessage: ChatMessage = {
            sender_id: message.sender_id,
            sender_name: message.sender_name,
            content: message.content,
            timestamp: message.timestamp || Date.now()
        };

        // Add to chat history if it exists
        if (this.gameState.chat_history) {
            this.gameState.chat_history.push(chatMessage);
        } else {
            this.gameState.chat_history = [chatMessage];
        }

        console.log('Chat message received:', {
            from: chatMessage.sender_name,
            message: chatMessage.content
        });

        // Emit chat message event
        this.emit('chat_message', chatMessage);
    }

    private sendConnectMessage(): void {
        const connectMessage = {
            type: 'connect',
            player_id: this.playerId,
            name: this.playerName,
            position: { x: 0, y: 0, z: 0 },
            rotation: { y: 0 },
            game_data: {
                name: this.playerName,
                isWalking: false
            }
        };

        console.log('Sending connect message:', connectMessage);
        this.sendMessage(connectMessage);
    }

    public updatePosition(position: Vector3, rotation: Rotation, gameData?: GameData): void {
        const updateMessage = {
            type: 'update',
            player_id: this.playerId,
            position,
            rotation,
            game_data: gameData || {
                name: this.playerName,
                isWalking: false
            }
        };

        this.sendMessage(updateMessage);
    }

    public sendGameMessage(message: any): void {
        const gameMessage = {
            type: 'game_message',
            player_id: this.playerId,
            message: JSON.stringify(message)
        };

        this.sendMessage(gameMessage);
    }

    /**
     * Sends a chat message to all connected players
     * @param content The text content of the chat message
     */
    public sendChatMessage(content: string): void {
        if (!content || content.trim() === '') {
            console.warn('Cannot send empty chat message');
            return;
        }

        const chatMessage = {
            type: 'chat_message',
            player_id: this.playerId,
            sender_id: this.playerId,
            sender_name: this.playerName,
            content: content.trim(),
            timestamp: Date.now()
        };

        console.log('Preparing to send chat message:', chatMessage);

        // Check if socket is ready
        if (this.socket?.readyState !== WebSocket.OPEN) {
            console.error('Cannot send chat message, socket not ready:', {
                readyState: this.socket?.readyState
            });
            return;
        }

        this.sendMessage(chatMessage);
        console.log('Chat message sent to server');
    }

    private sendMessage(message: any): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            const data = JSON.stringify(message);
            this.socket.send(data);
        } else {
            console.warn('Cannot send message, socket not ready:', {
                readyState: this.socket?.readyState,
                message
            });
        }
    }

    private startPingInterval(): void {
        this.pingInterval = setInterval(() => {
            this.sendMessage({
                type: 'ping',
                client_time: Date.now()
            });
        }, 1000);
    }

    private handleConnectionError(): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000); // Cap at 10 seconds
            console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay/1000} seconds...`);
            
            // Reset socket before reconnecting
            if (this.socket) {
                this.socket.close();
                this.socket = null;
            }
            
            setTimeout(() => {
                this.connect().catch(error => {
                    console.error('Reconnection attempt failed:', error);
                });
            }, delay);
        } else {
            console.error('Max reconnection attempts reached. Please check server status.');
            this.emit('disconnected');
            this.cleanup();
        }
    }

    private cleanup(): void {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }

        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
    }

    public disconnect(): void {
        this.sendMessage({ type: 'disconnect' });
        this.cleanup();
    }

    public getGameState(): GameState {
        return this.gameState;
    }

    public getPlayerId(): string {
        return this.playerId;
    }

    /**
     * Gets the chat history from the game state
     * @returns Array of chat messages or empty array if no history exists
     */
    public getChatHistory(): ChatMessage[] {
        return this.gameState.chat_history || [];
    }
} 